import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FileText, Loader2, Mail } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";

const fmtSize = (bytes) => (bytes ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : "");

export default function NewsletterSection() {
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subEmail, setSubEmail] = useState("");
  const [subscribing, setSubscribing] = useState(false);

  useEffect(() => {
    api
      .get("/newsletters")
      .then((res) => setIssues(res.data))
      .catch(() => setIssues([]))
      .finally(() => setLoading(false));
  }, []);

  const subscribe = async (e) => {
    e.preventDefault();
    setSubscribing(true);
    try {
      const res = await api.post("/subscribe", { email: subEmail });
      if (!res.data.already) trackEvent("newsletter_subscribe", { source: "newsletter_section" });
      toast.success(
        res.data.already
          ? "You're already on the list — see you next edition."
          : "Welcome aboard — the latest edition is on its way to your inbox."
      );
      setSubEmail("");
    } catch {
      toast.error("Could not subscribe right now — please try again.");
    } finally {
      setSubscribing(false);
    }
  };

  const featured = issues[0];
  const archive = issues.slice(1);

  return (
    <section id="newsletter" data-testid="newsletter-section" className="relative pt-10 pb-12 sm:pt-14 sm:pb-16 bg-[#100D12] overflow-hidden">
      <div className="absolute right-0 top-0 w-[26rem] h-[26rem] rounded-full bg-[#E890A7]/7 blur-[110px] pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9 }}
          className="mb-14"
        >
          <p className="font-mono text-xs tracking-[0.35em] uppercase text-[#E890A7]/80 mb-4">04 · The Monthly Edition</p>
          <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-tight max-w-2xl">
            The Lagoon City <em className="italic text-[#E890A7]">market report</em>
          </h2>
          <p className="mt-5 max-w-xl text-slate-400 font-light">
            Market pulse, new listings, community notes — penned by Joselle every month. Read any
            edition online, free.
          </p>
        </motion.div>

        {loading ? (
          <div className="flex items-center gap-3 text-slate-500" data-testid="newsletter-loading">
            <Loader2 className="animate-spin" size={18} /> Loading editions…
          </div>
        ) : !featured ? (
          <p className="text-slate-500" data-testid="newsletter-empty">
            The first edition is on its way — check back soon.
          </p>
        ) : (
          <>
            <motion.article
              data-testid={`newsletter-featured-${featured.newsletter_id}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.9 }}
              className="relative rounded-[2rem] border border-[#E890A7]/20 bg-gradient-to-br from-[#1E1A23] to-[#151218] p-8 sm:p-12 overflow-hidden rose-glow"
            >
              <div className="absolute -right-10 -bottom-16 font-display italic text-[11rem] leading-none text-[#E890A7]/6 select-none pointer-events-none">
                {String(featured.month).padStart(2, "0")}
              </div>
              <div className="relative grid md:grid-cols-[1fr_auto] gap-10 items-center">
                <div>
                  <p className="font-mono text-[10px] tracking-[0.35em] uppercase text-[#F2A7BA] mb-4 flex items-center gap-2">
                    <FileText size={13} /> Latest Issue · {fmtSize(featured.file_size)}
                  </p>
                  <h3 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl">
                    {featured.month_name} <span className="text-[#E890A7]">{featured.year}</span>
                  </h3>
                  <p className="mt-4 text-slate-400 max-w-lg font-light">
                    {featured.title} — fresh listings, lagoon market stats and what&apos;s happening
                    around the canals this month.
                  </p>
                  <div className="mt-8 flex flex-wrap gap-4">
                    <Link
                      data-testid={`newsletter-view-btn-${featured.newsletter_id}`}
                      to={`/blog/${featured.newsletter_id}`}
                      onClick={() => trackEvent("view_market_report", { edition: `${featured.month_name} ${featured.year}` })}
                      className="rounded-full bg-[#E890A7] text-[#0D0B0E] font-semibold px-7 py-3.5 flex items-center gap-2 hover:bg-[#F2A7BA] hover:-translate-y-0.5 transition-all duration-300"
                    >
                      <FileText size={16} /> View report
                    </Link>
                  </div>
                </div>
                <div className="hidden md:flex flex-col items-center gap-2 rounded-2xl border border-[#E890A7]/15 bg-[#0D0B0E]/60 px-8 py-6">
                  <span className="font-display text-5xl text-[#E890A7]">{featured.downloads}</span>
                  <span className="text-[10px] font-mono tracking-widest uppercase text-slate-500">downloads</span>
                </div>
              </div>
            </motion.article>

            <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="newsletter-archive">
              {archive.map((n, i) => (
                <motion.article
                  key={n.newsletter_id}
                  data-testid={`newsletter-card-${n.newsletter_id}`}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.7, delay: i * 0.07 }}
                  className="rounded-3xl border border-[#E890A7]/12 bg-[#151218] p-6 hover:border-[#E890A7]/40 transition-colors duration-300"
                >
                  <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-slate-500">
                    {fmtSize(n.file_size)} · {n.downloads} downloads
                  </p>
                  <h4 className="font-display text-2xl mt-2">
                    {n.month_name} {n.year}
                  </h4>
                  <div className="mt-5 flex gap-3">
                    <Link
                      data-testid={`newsletter-view-btn-${n.newsletter_id}`}
                      to={`/blog/${n.newsletter_id}`}
                      onClick={() => trackEvent("view_market_report", { edition: `${n.month_name} ${n.year}` })}
                      className="flex-1 rounded-full border border-[#E890A7]/35 text-[#F2A7BA] text-sm text-center px-4 py-2.5 hover:bg-[#E890A7]/10 transition-colors"
                    >
                      View report
                    </Link>
                  </div>
                </motion.article>
              ))}
              <div className="rounded-3xl border border-dashed border-[#E890A7]/20 p-6 flex flex-col justify-center">
                <p className="font-display italic text-2xl text-[#E890A7]/70">A new edition every month.</p>
                <p className="text-sm text-slate-500 mt-2">
                  Next issue lands in {featured.month === 12 ? "January" : ["January","February","March","April","May","June","July","August","September","October","November","December"][featured.month]} — check back soon.
                </p>
              </div>
            </div>

            <motion.div
              data-testid="newsletter-subscribe-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.8 }}
              className="mt-8 rounded-[2rem] border border-[#E890A7]/25 bg-gradient-to-r from-[#1E1A23] to-[#151218] p-8 sm:p-10"
            >
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div>
                  <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#E890A7] mb-3 flex items-center gap-2">
                    <Mail size={13} /> Inbox delivery
                  </p>
                  <h3 className="font-display text-2xl sm:text-3xl">Get every edition, automatically</h3>
                  <p className="text-sm text-slate-400 mt-2 font-light">
                    Leave your email and each new monthly issue lands in your inbox the moment
                    Joselle publishes it. No spam — one email a month.
                  </p>
                </div>
                <form data-testid="newsletter-subscribe-form" onSubmit={subscribe} className="flex flex-col sm:flex-row gap-3">
                  <input
                    data-testid="newsletter-subscribe-email"
                    type="email"
                    required
                    value={subEmail}
                    onChange={(e) => setSubEmail(e.target.value)}
                    placeholder="you@email.com"
                    className="flex-1 rounded-full bg-[#0D0B0E]/70 border border-[#E890A7]/25 px-5 py-3.5 text-sm text-slate-100 placeholder:text-slate-600 focus:outline-none focus:border-[#E890A7]/70 transition-colors"
                  />
                  <button
                    data-testid="newsletter-subscribe-btn"
                    type="submit"
                    disabled={subscribing}
                    className="rounded-full bg-[#E890A7] text-[#0D0B0E] font-semibold px-7 py-3.5 text-sm flex items-center justify-center gap-2 hover:bg-[#F2A7BA] disabled:opacity-60 transition-colors"
                  >
                    {subscribing && <Loader2 size={15} className="animate-spin" />}
                    {subscribing ? "Joining…" : "Subscribe"}
                  </button>
                </form>
              </div>
            </motion.div>
          </>
        )}
      </div>
    </section>
  );
}
